import React from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Button } from '@material-ui/core';
import ScoreCard from '../ScoreCard';
import Icons from '../Icons';
import { getCountryBasedLink, forwardTo } from '../../../utils/helper';
import I18n from '../../../config/locales';
import feature from '../../../utils/feature';
import { isEmpty } from '../../../utils/lodash';
import analytics from '../../../utils/analytics';
import { getCountryCode } from '../helper';
import { getPlatformBasedUrl } from '../../../config/platformBasedUrls/url';

const useStyles = makeStyles(theme => ({
  scoreCardBody: {
    display: 'flex',
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
      gridRowGap: theme.spacing(2.5)
    }
  },
  scoreCardWrapper: {
    boxShadow: '0px 0px 8px #00000029',
    position: 'relative',
    width: '100%'
  },
  scoreCardHeader: {
    color: '#43425D',
    fontSize: 15,
    fontWeight: 'bold',
    padding: theme.spacing(1, 0, 0, 2),
    width: 'calc(100% - 100px)'
  },
  tierIcon: {
    width: theme.spacing(12.5),
    height: theme.spacing(15),
    position: 'absolute',
    right: 0,
    left: 0,
    margin: 'auto',
    top: theme.spacing(-1)
  },
  bodyWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 4),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center'
    }
  },
  learnButton: {
    minWidth: theme.spacing(22.5),
    color: theme.palette.secondary.main,
    margin: theme.spacing(4, 0)
  },
  tierIconWrapper: {
    height: theme.spacing(18.5),
    maxWidth: theme.spacing(25),
    width: '100%',
    marginLeft: theme.spacing(2.5),
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
      marginTop: theme.spacing(3),
      maxWidth: 'none'
    }
  },
  scoreCardContainer: {
    display: 'flex',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column'
    }
  },
  viewEinDashboard: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    height: 'calc(100% - 8px)',
    color: '#3B86FF',
    fontSize: 15,
    cursor: 'pointer'
  }
}));

const ScoreCardPanel = props => {
  const gaAnalytics = new analytics();
  const {
    tier,
    show_ein_learn_more_link,
    installingSince,
    ensembleCertification,
    npsScore,
    sitesCount,
    invertersCount,
    storagesCount,
    period = 12,
    belongsToUs,
    featureList = [],
    belongsToIn,
    belongsToMx,
    countryCode,
    npsScoreUpdated = true,
    isUserDemoRole,
    showEinBadge,
    isAdministrator
  } = props;
  const classes = useStyles();
  const hasTier = !!tier;
  const ensembleCertified =
    belongsToUs ||
    belongsToMx;
  const country = getCountryCode(countryCode);

  const TierIconTemplate = hasTier ? Icons[tier.toLowerCase()] : '';
  const showEinDashboard = !isEmpty(featureList) ? feature.isFeatureEnabled('EIN_DASHBOARD') && !isUserDemoRole : false;
  const viewEinDashboard = () => {
    gaAnalytics.triggerEvent({ event: 'triggerViewEinDashboard' }, {});
    forwardTo(getPlatformBasedUrl('dashboard.viewEinDashboard'));
  };
  return (
    <Box className={classes.scoreCardContainer}>
      {isAdministrator && (
        <Box className={classes.scoreCardWrapper} id="scoreCardBox">
          <Typography className={classes.scoreCardHeader}>{I18n.t('dashboard.companyScoreCard')}</Typography>
          <Box className={classes.bodyWrapper}>
            <Box className={classes.scoreCardBody}>
              <ScoreCard
                value={installingSince}
                label={I18n.t('dashboard.enphaseSince')}
                icon="calender"
                valueId="installingSinceValue"
              />
              <ScoreCard
                value={sitesCount}
                label={I18n.t('dashboard.sitesCommissioned')}
                period={period}
                icon="home"
                valueId="sitesCommissionedValue"
              />
              <ScoreCard
                value={invertersCount}
                label={I18n.t('dashboard.microinverters')}
                period={period}
                icon="energy"
                valueId="microInvertersInstalledValue"
              />
              {!belongsToIn && (
                <ScoreCard
                  value={storagesCount}
                  label={I18n.t('dashboard.batteriesInstalled')}
                  period={period}
                  icon="battery"
                  valueId="batteriesInstalledValue"
                />
              )}
              <ScoreCard
                value={npsScore}
                npsScoreUpdated={npsScoreUpdated}
                label={I18n.t('dashboard.nps')}
                icon="smiley"
                valueId="npsValue"
              />
              {ensembleCertified && (
                <ScoreCard
                  value={ensembleCertification}
                  label={I18n.t('dashboard.ensembleCertified')}
                  icon="bookmark"
                  valueId="ensembleCertifiedValue"
                />
              )}
            </Box>
            {!hasTier && show_ein_learn_more_link && (
              <Button
                className={classes.learnButton}
                size="small"
                variant="contained"
                color="primary"
                onClick={() => window.open(getCountryBasedLink('aboutInstaller'), '_blank')}
                disableElevation
              >
                {I18n.t('dashboard.einLearn')}
              </Button>
            )}
          </Box>
        </Box>
      )}
      {showEinBadge && TierIconTemplate && (
        <Box className={clsx(classes.tierIconWrapper, classes.scoreCardWrapper)} id="viewEINDashboardBox">
          <TierIconTemplate className={classes.tierIcon} countryCode={country} />
          {showEinDashboard && (
            <Typography
              component="div"
              className={classes.viewEinDashboard}
              onClick={() => viewEinDashboard()}
              id="viewEINDashboard"
            >
              {I18n.t('dashboard.viewEinDashboard')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const mapDispatchToProps = dispatch => ({});

const mapStateToProps = state => ({
  belongsToIn: state.appReducer.belongsToIn,
  belongsToMx: state.appReducer.belongsToMx,
  belongsToDe: state.appReducer.belongsToDe,
  belongsToBe: state.appReducer.belongsToBe,
  belongsToNl: state.appReducer.belongsToNl,
  belongsToNz: state.appReducer.belongsToNz,
  belongsToNc: state.appReducer.belongsToNc,
  belongsToFr: state.appReducer.belongsToFr,
  countryCode: state.appReducer.countryCode
});

export default connect(mapStateToProps, mapDispatchToProps)(ScoreCardPanel);
